import styled from "styled-components";
import type { Dokumentti, DokumenttiTyyppi } from "../types";
import { useTranslation } from "react-i18next";
import { getDokumenttiTila } from "../utils/dokumentit";
import DokumenttiKuvat from "./DokumenttiKuvat";
import { alpha2ToAlpha3 } from "../utils/kansalaisuudet";
import countries from "i18n-iso-countries";
import fi from "i18n-iso-countries/langs/fi.json";
import en from "i18n-iso-countries/langs/en.json";
import { useState } from "react";
import { Button } from "suomifi-ui-components";
import { IconArrowRight } from "suomifi-icons";

// Placeholder kun sirulta luettua tietoa ei (vielä) ole saatavilla.
const DASH = "—";

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $invalid: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.m};
  padding: ${({ theme }) => theme.spacing.s} 0;

  &:first-child {
    padding-top: 0;
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TyyppiName = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBold};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

/* Kaksi palstaa riittävän leveällä KORTILLA (ei viewportilla): henkilötiedot
   (perustiedot) vasemmalla, asiakirjan hallinnolliset tiedot oikealla.
   auto-fit + minmax → kaksi palstaa vain kun kortti oikeasti mahtuu (huomioi
   sivupalkin viemän tilan), muuten yksi palsta allekkain. Palstan minimi 19rem
   = 10rem label + ~9rem arvo, jotta arvot eivät romahda (ks. kenttätutkimus).
   Palstojen väli (row-gap) toimii samana "tyhjänä rivinä" joka erottaa ryhmät. */
const Palstat = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(19rem, 1fr));
  column-gap: ${({ theme }) => theme.spacing.xl};
  row-gap: ${({ theme }) => theme.spacing.m};
  align-items: start;
`;

const Palsta = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Label/tieto-rivi: 2-col-grid, 10rem semibold-label vasemmalla, arvo oikealla. */
const Rivi = styled.div`
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: ${({ theme }) => theme.spacing.s};
  margin-top: ${({ theme }) => theme.spacing.s};
  align-items: start;

  &:first-child {
    margin-top: 0;
  }
`;

export interface IdentityDocumentInImportedFormat {
  expiryDate: string;
  issuingCountry: string;
  issuingDate: string;
  number: string;
  type: string;
  originalCreationDate: string;
}

const FactKey = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const FactVal = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  /* Rivittää tarvittaessa sanarajoilla; pilkkoo yksittäisen ylipitkän merkkijonon
     vain jos se muuten ylivuotaisi (ei merkki kerrallaan kuten word-break).
     Säilyttää sana-tason minimileveyden → lyhyet arvot pysyvät yhdellä rivillä. */
  overflow-wrap: break-word;
`;

const ValidPill = styled.span<{ $valid: boolean }>`
  flex-shrink: 0;
  background: ${({ theme, $valid }) =>
    $valid ? theme.colors.successDark1 : theme.colors.alertBase};
  color: ${({ theme }) => theme.colors.whiteBase};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
`;

const ButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.m};
`;

function tilaLabel(tila: string): string {
  if (tila === "voimassa") return "Voimassa";
  if (tila === "vanhentunut") return "Vanhentunut";
  return "Virheellinen";
}

countries.registerLocale(fi);
countries.registerLocale(en);

// Placeholder-asiakirja: renderöi kenttäskeletonin pelkin viivoin ("—"), kun
// asiakirjaa ei ole populoitu mutta halutaan silti näyttää uusin asettelu
// (tukipyynnön "Asiakirjalta luetut tiedot"). Tyyppiotsikko ja tagi jätetään pois.
const TYHJA_DOKUMENTTI: Dokumentti = {
  tyyppi: "passi",
  maakoodi: "",
  numero: "",
  vanhenee: "",
  tila: "voimassa",
};

export default function Dokumentit({
  dokumentit,
  uid,
  identityDocument,
  naytaTag = true,
  naytaViivatKunTyhja = false,
}: {
  dokumentit: Dokumentti[];
  identityDocument?: IdentityDocumentInImportedFormat;
  uid: string;
  naytaTag?: boolean;
  naytaViivatKunTyhja?: boolean;
}) {
  const { t, i18n } = useTranslation(["kayttajaKortti", "common"]);
  const [naytaVanhat, setNaytaVanhat] = useState(false);

  const onPlaceholder = dokumentit.length === 0 && naytaViivatKunTyhja;
  const naytettavat =
    dokumentit.length === 0 && identityDocument
      ? [
          {
            tyyppi: (identityDocument.type as DokumenttiTyyppi) || "passi",
            maakoodi: identityDocument.issuingCountry || "",
            numero: identityDocument.number || "",
            vanhenee: identityDocument.expiryDate || "",
            tila: getDokumenttiTila(identityDocument.expiryDate || ""),
          } as Dokumentti,
        ]
      : dokumentit.length === 0 && naytaViivatKunTyhja
      ? [TYHJA_DOKUMENTTI]
      : dokumentit;

  if (naytettavat.length === 0) {
    return <Empty>{t("ei.dokumentteja")}</Empty>;
  }

  const reversedDokumentit = [...naytettavat].reverse();
  const naytettavatDocumentit = naytaVanhat
    ? reversedDokumentit
    : [reversedDokumentit[0]];

  return (
    <List>
      {naytettavatDocumentit.map((d, i) => {
        const tila = getDokumenttiTila(d.vanhenee);
        const voimassa = tila === "voimassa";
        const maa = countries.getName(d.maakoodi, i18n.language);

        const kansalaisuus = d.kansalaisuus
          ? `${countries.getName(
              d.kansalaisuus,
              i18n.language,
            )} (${alpha2ToAlpha3(d.kansalaisuus)})`
          : DASH;
        const sukupuoli = d.sukupuoli
          ? t(`common:sukupuolet.${d.sukupuoli}`)
          : DASH;
        const myontajamaa = d.maakoodi ? `${maa} (${d.maakoodi})` : DASH;
        const alpha3Code = alpha2ToAlpha3(d.maakoodi);

        // ✅ Get tyyppiLabel using t from component level
        const tyyppiLabelText =
          d.tyyppi === "passi"
            ? t(`dokumenttiTyypit.${d.tyyppi}`)
            : d.tyyppi === "henkilökortti"
            ? t(`dokumenttiTyypit.henkilokortti`)
            : d.tyyppi;

        return (
          <>
            <Row key={i} $invalid={!voimassa}>
              <Body>
                {!onPlaceholder && <TyyppiName>{tyyppiLabelText}</TyyppiName>}
                <Palstat>
                  {/* Vasen palsta: henkilötiedot (perustiedot) */}
                  <Palsta>
                    <Rivi>
                      <FactKey>{t("common:sukunimi")}</FactKey>
                      <FactVal>{d.sukunimi || DASH}</FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:etunimet")}</FactKey>
                      <FactVal>{d.etunimi || DASH}</FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:syntymaaika")}</FactKey>
                      <FactVal>{d.syntymaaika || DASH}</FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:sukupuoli")}</FactKey>
                      <FactVal>{sukupuoli}</FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:kansalaisuus")}</FactKey>
                      <FactVal>{kansalaisuus}</FactVal>
                    </Rivi>
                  </Palsta>

                  {/* Oikea palsta: asiakirjan hallinnolliset tiedot */}
                  <Palsta>
                    <Rivi>
                      <FactKey>{t("common:asiakirjanNumero")}</FactKey>
                      <FactVal>{d.numero || DASH}</FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:myontajamaa")}</FactKey>
                      <FactVal>
                        {maa}{" "}
                        {alpha3Code.length > 2 ? alpha3Code : `(${alpha3Code})`}
                      </FactVal>
                    </Rivi>
                    <Rivi>
                      <FactKey>{t("common:voimassaolopaiva")}</FactKey>
                      <FactVal>{d.vanhenee || DASH}</FactVal>
                    </Rivi>
                  </Palsta>
                </Palstat>
              </Body>
              {naytaTag && !onPlaceholder && (
                <ValidPill $valid={voimassa}>{tilaLabel(tila)}</ValidPill>
              )}
            </Row>
            <DokumenttiKuvat
              uid={uid}
              dokumenttiEtupuoliFileName={d.dokumenttiEtupuoliFileName}
              dokumenttiTakapuoliFileName={d.dokumenttiTakapuoliFileName}
              selfieFileName={d.selfieFileName}
            />
          </>
        );
      })}

      {/* ✅ Show button if there are older documents */}
      {reversedDokumentit.length > 1 && !naytaVanhat && (
        <ButtonWrapper>
          <Button
            onClick={() => setNaytaVanhat(true)}
            variant="secondary"
            iconRight={<IconArrowRight />}
          >
            {t("labels.naytaVanhatDokumentit")} ({reversedDokumentit.length - 1}
            )
          </Button>
        </ButtonWrapper>
      )}
    </List>
  );
}
